import React from 'react'
import { HoursInput } from '../../HourlyInvoice/InvoiceSecondStep'
import { useHourlyUpdateInvoice } from './context/HourlyUpdateInvoiceContext'
import { partyLabelFor } from './partyLabel'

/**
 * Step 2 — the line items.
 *
 * CHROME IS CM'S. The teal "Line Items" heading, the bordered table with
 * Description / Hours:Minutes / Rate ($/hr) / Amount, the Subtotal strip, the
 * native date input and the Notes textarea are the same ones the hourly CREATE
 * flow (HourlyInvoice/InvoiceSecondStep) and the fixed editor
 * (UpdateInvoice/Step2UpdateInvoice) already render. `HoursInput` is imported
 * from the create step rather than reimplemented so the HH:MM parsing, the
 * blur-normalisation and the 4-digit edge case stay byte-identical.
 *
 * THREE DELIBERATE DIFFERENCES FROM CREATE
 *
 * 1. There is ONE editor and no party switcher, however many invoices are
 *    selected. The line items ARE the case-wide mediation ledger and every
 *    selected invoice bills a share of the same session, so a per-party tab
 *    would suggest separate bills are being authored.
 *
 * 2. "Invoice Allocation by Party" is not offered. The allocation is already
 *    baked into each invoice's stored share, which is what the party is being
 *    billed against right now; re-splitting it would change what someone owes
 *    on an invoice already sitting in their inbox.
 *
 * 3. The due date has NO `min`. This is an EDIT: existing invoices routinely
 *    carry a due date that has already passed, and a minimum would make the
 *    current value unpickable. The server moves an expired date forward when it
 *    reissues.
 *
 * Payment Reminders is likewise absent: the update endpoint rebuilds the unsent
 * ladder from the new due date and does not accept a schedule.
 */

function fmtMoney(n) {
    const v = Number(n) || 0
    return v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

const round2 = (n) => Math.round((Number(n) || 0) * 100 + Number.EPSILON) / 100

const LineItemEditor = ({ row, shared }) => {
    const {
        itemsByInvoice,
        setItemsFor,
        setItemsForAll,
        dueDateByInvoice,
        setDueDateFor,
        setDueDateForAll,
        notesByInvoice,
        setNotesFor,
        setNotesForAll,
        subtotalFor,
        targetRows,
        breakdownFor,
        caseInfo,
        mediatorRecord,
    } = useHourlyUpdateInvoice()

    /* Under a shared edit every keystroke lands on all the selected invoices,
     * so they can never carry different ledgers. */
    const writeItems = shared ? setItemsForAll : (v) => setItemsFor(row.id, v)
    const writeDueDate = shared ? setDueDateForAll : (v) => setDueDateFor(row.id, v)
    const writeNotes = shared ? setNotesForAll : (v) => setNotesFor(row.id, v)

    const items = itemsByInvoice[row.id] || []
    const subtotal = subtotalFor(row.id)
    const recovered = items.some((it) => it._recovered)
    const dueDate = dueDateByInvoice[row.id] || ''

    const patchItem = (index, patch) => {
        writeItems(
            items.map((it, i) => {
                if (i !== index) return it
                const merged = { ...it, ...patch }
                /* Clamp at zero — a negative line could hide in a positive
                 * total. */
                if (patch.quantity !== undefined && Number(merged.quantity) < 0) {
                    merged.quantity = 0
                }
                if (patch.rate !== undefined && Number(merged.rate) < 0) merged.rate = 0
                merged.amount = round2(
                    (Number(merged.quantity) || 0) * (Number(merged.rate) || 0)
                )
                return merged
            })
        )
    }

    const addItem = () => {
        const lastRate = items[items.length - 1]?.rate || 0
        const nextId = items.reduce((m, it) => Math.max(m, Number(it.id) || 0), 0) + 1
        writeItems([
            ...items,
            {
                id: nextId,
                description: '',
                quantity: 1,
                rate: lastRate,
                amount: lastRate,
            },
        ])
    }

    const removeItem = (id) => writeItems(items.filter((it) => it.id !== id))

    /* What each party actually ends up owing, from the same resolution the
     * backend bills from. Collapsed to one figure when they all match — the
     * common case — and to a range when the shares differ, so the sentence
     * stays one line however many parties there are. */
    const partyTotals = targetRows.map((r) => breakdownFor(r, subtotal).total)
    const uniqueTotals = [...new Set(partyTotals.map((t) => t.toFixed(2)))]
    const partyTotalsSentence = !partyTotals.length
        ? ''
        : targetRows.length === 1
          ? `This party is billed $${fmtMoney(partyTotals[0])}.`
          : uniqueTotals.length === 1
            ? `Each party is billed $${fmtMoney(partyTotals[0])} of it.`
            : `Each party is billed their share, from $${fmtMoney(
                  Math.min(...partyTotals)
              )} to $${fmtMoney(Math.max(...partyTotals))}.`

    return (
        <div className="space-y-5">
            <div>
                <p className="font-semibold text-gray-900 mb-1">
                    Applies to:{' '}
                    {targetRows.length > 1
                        ? `All ${targetRows.length} parties`
                        : partyLabelFor(row, caseInfo, mediatorRecord)}
                </p>
                {targetRows.length === 1 && (
                    /* The CURRENT billed figure, labelled as such. The sentence
                       below is what the edit will produce; showing both
                       unlabelled is what reads as "the totals do not match". */
                    <p className="text-sm text-gray-500">
                        Current amount:{' '}
                        <span className="font-semibold">${fmtMoney(row.amount)}</span>
                    </p>
                )}
                <p className="text-sm text-gray-500">
                    {targetRows.length > 1
                        ? `These line items are the whole mediation session and apply to all ${targetRows.length} invoices. ${partyTotalsSentence}`
                        : partyTotalsSentence || 'Add invoice details and line items.'}
                </p>
            </div>

            {/* A quarter of live hourly invoices never stored their line items.
                Say the line was rebuilt rather than letting it pass as what the
                client saw. */}
            {recovered && (
                <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
                    <span className="text-amber-500 mt-0.5 shrink-0">
                        <svg width="15" height="15" viewBox="0 0 20 20" fill="currentColor">
                            <path d="M10 2a8 8 0 100 16 8 8 0 000-16zm0 4a1 1 0 011 1v4a1 1 0 11-2 0V7a1 1 0 011-1zm0 8.5a1.1 1.1 0 110-2.2 1.1 1.1 0 010 2.2z" />
                        </svg>
                    </span>
                    <span className="text-xs text-gray-700 leading-relaxed">
                        {targetRows.length > 1
                            ? `The original line items were not stored, so this opens with a single line rebuilt from the invoice total. Whatever you enter replaces the items on all ${targetRows.length} invoices.`
                            : 'The original line items were not stored, so this opens with a single line rebuilt from the invoice total. Adjust it before saving.'}
                    </span>
                </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                        Due Date
                    </label>
                    <input
                        type="date"
                        value={dueDate}
                        onChange={(e) => writeDueDate(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                    />
                </div>
            </div>

            <div className="space-y-2">
                <p className="font-semibold text-teal-800">Line Items</p>
                <div className="overflow-x-auto border border-gray-200 rounded-lg">
                    <table className="w-full text-sm">
                        <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                            <tr>
                                <th className="px-3 py-2 text-left">Description</th>
                                <th className="px-3 py-2 text-left w-28">Hours:Minutes</th>
                                <th className="px-3 py-2 text-left w-28">Rate ($/hr)</th>
                                <th className="px-3 py-2 text-right w-32">Amount</th>
                                <th className="px-3 py-2 w-10"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((it, i) => (
                                <tr key={it.id ?? i} className="border-t border-gray-200">
                                    <td className="px-3 py-2">
                                        <input
                                            type="text"
                                            value={it.description}
                                            onChange={(e) =>
                                                patchItem(i, {
                                                    description: e.target.value,
                                                })
                                            }
                                            placeholder="eg. Mediation Prep"
                                            className="w-full px-2 py-1.5 border border-gray-300 rounded"
                                        />
                                    </td>
                                    <td className="px-3 py-2">
                                        <HoursInput
                                            valueDecimal={it.quantity}
                                            onChangeDecimal={(v) =>
                                                patchItem(i, { quantity: v })
                                            }
                                        />
                                    </td>
                                    <td className="px-3 py-2">
                                        <div className="relative">
                                            <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 text-xs">
                                                $
                                            </span>
                                            <input
                                                type="number"
                                                min={0}
                                                step={1}
                                                value={it.rate}
                                                onChange={(e) =>
                                                    patchItem(i, {
                                                        rate:
                                                            parseFloat(e.target.value) ||
                                                            0,
                                                    })
                                                }
                                                className="w-full pl-5 pr-2 py-1.5 border border-gray-300 rounded"
                                            />
                                        </div>
                                    </td>
                                    <td className="px-3 py-2 text-right text-gray-700">
                                        ${fmtMoney(it.amount)}
                                    </td>
                                    <td className="px-3 py-2 text-right">
                                        {items.length > 1 ? (
                                            <button
                                                type="button"
                                                aria-label="Remove line"
                                                onClick={() => removeItem(it.id)}
                                                className="text-gray-400 hover:text-red-500"
                                            >
                                                ×
                                            </button>
                                        ) : null}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                        <tfoot className="bg-gray-50">
                            <tr>
                                <td
                                    colSpan={3}
                                    className="px-3 py-2 text-right text-xs uppercase text-gray-500"
                                >
                                    Subtotal
                                </td>
                                <td
                                    className="px-3 py-2 text-right font-semibold"
                                    style={{ color: '#027776' }}
                                >
                                    ${fmtMoney(subtotal)}
                                </td>
                                <td></td>
                            </tr>
                        </tfoot>
                    </table>
                </div>
                <button
                    type="button"
                    onClick={addItem}
                    className="mt-2 text-sm font-medium"
                    style={{ color: '#115E59' }}
                >
                    + Add Item
                </button>
            </div>

            <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                    Notes
                </label>
                <textarea
                    value={notesByInvoice[row.id] ?? ''}
                    onChange={(e) => writeNotes(e.target.value)}
                    rows={3}
                    placeholder="Additional Notes"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                />
            </div>
        </div>
    )
}

/**
 * ONE editor, however many invoices are selected — see the note above on why
 * there is no party switcher here.
 */
export default function Step2() {
    const { targetRows } = useHourlyUpdateInvoice()
    const anchor = targetRows[0]
    if (!anchor) return null
    return <LineItemEditor key={anchor.id} row={anchor} shared={targetRows.length > 1} />
}
